import { useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { stats, testimonials } from '@/mocks/data';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }
    const formData = new FormData(form);
    const submitAddr = 'https://readdy.ai/api/form/d9phksti2vhsdh2d34lg';
    try {
      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试。');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络连接异常，请检查网络后重试。');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于17c',
    description: '17c品牌介绍、口碑评价与联系方式。了解17c的核心使命、发展历程和用户口碑。',
    url: 'https://17c.com/about',
    mainEntity: {
      '@type': 'Organization',
      name: '17c',
      url: 'https://17c.com',
      description: '17c - 核心数字体验平台',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '400-888-17C1',
        contactType: 'customer service',
        email: 'support@17c.com',
      },
    },
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 了解17c平台"
        description="17c品牌介绍、口碑评价与联系方式。了解17c的核心使命、发展历程和用户口碑，立即联系我们了解更多。"
        keywords="17c,关于17c,17c平台介绍,17c联系方式,17c品牌"
        canonicalUrl="/about"
        schemaJson={aboutSchema}
      />
      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        <section className="py-16 md:py-24">
          <div className="w-full max-w-[900px] mx-auto px-6 md:px-10 text-center">
            <p className="text-sm text-primary-500 font-medium mb-3 tracking-wide">关于我们</p>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 mb-6">
              关于<span className="text-primary-500">17c</span>
            </h1>
            <p className="text-lg text-foreground-600 leading-relaxed max-w-[700px] mx-auto">
              17c诞生于对数字化体验的深刻洞察。我们致力于打造一个集内容管理、数据分析与团队协作于一体的核心体验平台，帮助每一位用户释放数字潜能。
            </p>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-100">
          <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {stats.map((stat, index) => (
                <div key={index}>
                  <div className="text-3xl md:text-4xl font-heading font-bold text-primary-500 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10">
            <div className="text-center mb-14">
              <p className="text-sm text-primary-500 font-medium mb-3 tracking-wide">品牌故事</p>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                我们的使命与愿景
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-[1000px] mx-auto">
              <div className="p-8 bg-background-50 border border-background-200/70 rounded-lg text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 mx-auto mb-5">
                  <i className="ri-lightbulb-line text-2xl" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-3">创新驱动</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们始终站在技术前沿，不断探索和引入最新技术，为用户提供最具创新性的数字体验工具。
                </p>
              </div>
              <div className="p-8 bg-background-50 border border-background-200/70 rounded-lg text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mx-auto mb-5">
                  <i className="ri-user-heart-line text-2xl" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-3">用户至上</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  每一个功能的设计都以用户需求为出发点，持续收集反馈，不断优化产品体验。
                </p>
              </div>
              <div className="p-8 bg-background-50 border border-background-200/70 rounded-lg text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 mx-auto mb-5">
                  <i className="ri-shield-star-line text-2xl" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-3">安全可靠</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们将数据安全视为生命线，通过严苛的安全标准和合规认证，守护每一位用户的信任。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-background-100">
          <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10">
            <div className="text-center mb-14">
              <p className="text-sm text-primary-500 font-medium mb-3 tracking-wide">用户口碑</p>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                他们都在用17c
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t, index) => (
                <div key={index} className="p-6 md:p-8 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-400 text-sm w-4 h-4 flex items-center justify-center" />
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-5">&ldquo;{t.content}&rdquo;</p>
                  <div className="flex items-center gap-3 pt-4 border-t border-background-200/70">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-500 font-heading font-bold text-sm">
                      {t.name[0]}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}，{t.company}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10">
            <div className="flex flex-col lg:flex-row gap-14">
              <div className="flex-1">
                <p className="text-sm text-primary-500 font-medium mb-3 tracking-wide">联系我们</p>
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                  与我们取得联系
                </h2>
                <p className="text-foreground-600 leading-relaxed mb-8">
                  无论您是想了解更多关于17c的信息，还是需要技术支持，我们的团队都随时准备为您提供帮助。
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-500">
                      <i className="ri-mail-line" />
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">邮箱</p>
                      <p className="text-sm font-medium text-foreground-900">support@17c.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-500">
                      <i className="ri-phone-line" />
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">电话</p>
                      <p className="text-sm font-medium text-foreground-900">400-888-17C1</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-500">
                      <i className="ri-map-pin-line" />
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">地址</p>
                      <p className="text-sm font-medium text-foreground-900">北京市朝阳区科技园区17号</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
                  <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-6">发送消息</h3>

                  {formStatus === 'success' && (
                    <div className="mb-6 p-4 bg-accent-100 text-accent-700 rounded-md text-sm">
                      感谢您的消息！我们会尽快与您联系。
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-md text-sm">
                      {formError}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} data-readdy-form="true" className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        消息
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        required
                        maxLength={500}
                        className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                        placeholder="请输入您的消息（最多500字）"
                      />
                      <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                    </div>
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="website-alt-field"
                    />
                    <button
                      type="submit"
                      className="w-full py-3 bg-primary-500 text-background-50 rounded-md text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      发送消息
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}